import { Globe, MoreHorizontal, ThumbsUp, MessageCircle, Share } from 'lucide-react';
import type { CreativeAsset, MetaAdDraftInput } from '../../../types/metaAdDraft';
import { ctaLabel } from '../ctaOptions';
import { firstNonEmpty, rotationLabel, truncate } from '../mockupText';

// D3.4 — render a Feed/Story asset preview. Image → <img>. Video → its thumbnail
// if set, else a muted <video> showing the first frame. Empty → placeholder text.
function AssetPreview({ asset, placeholder }: { asset: CreativeAsset; placeholder: string }) {
  if (!asset.url) {
    return (
      <div className="flex h-full w-full items-center justify-center text-sm text-slate-400">{placeholder}</div>
    );
  }
  if (asset.type === 'video') {
    return asset.thumbnailUrl ? (
      <img src={asset.thumbnailUrl} alt="" className="h-full w-full object-cover" />
    ) : (
      <video src={asset.url} className="h-full w-full object-cover" muted playsInline preload="metadata" />
    );
  }
  return <img src={asset.url} alt="" className="h-full w-full object-cover" />;
}

// Stage 7A D1 — Meta-styled ad mockup. Deliberately STYLIZED (brand-recognizable,
// not a pixel-perfect replica) so it survives Meta UI changes. Live: re-renders
// from the draft input on every keystroke.

const PRIMARY_TEXT_FOLD = 125;

function Avatar({ name }: { name: string }) {
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-300 text-sm font-semibold text-slate-600">
      {initial}
    </div>
  );
}

export function AdMockup({
  input,
  pageName,
}: {
  input: MetaAdDraftInput;
  pageName: string | null;
}) {
  const creative = input.ads.creative;
  const displayPage = pageName?.trim() || 'Your Page';

  const primaryRaw = firstNonEmpty(creative.primaryTexts);
  const primary = truncate(primaryRaw, PRIMARY_TEXT_FOLD);
  const headline = firstNonEmpty(creative.headlines);
  const description = firstNonEmpty(creative.descriptions);
  const cta = ctaLabel(creative.callToAction);

  const headlineN = (creative.headlines ?? []).filter((h) => h.trim()).length;
  const primaryN = (creative.primaryTexts ?? []).filter((p) => p.trim()).length;

  const feedAsset = creative.feedAsset;
  const storyAsset = creative.storyAsset;
  const hasStory = typeof storyAsset.url === 'string' && storyAsset.url.trim() !== '';

  // Destination domain shown above the headline (Meta's display link line).
  const dest = input.ads.destination;
  let displayLink = '';
  if (dest.type === 'website' && dest.url.trim()) {
    try {
      displayLink = new URL(dest.url).hostname.replace(/^www\./, '').toUpperCase();
    } catch {
      displayLink = '';
    }
  } else if (dest.type === 'instant_form') {
    displayLink = 'INSTANT FORM ON FACEBOOK';
  }

  return (
    <div className="space-y-6">
      {/* ---------- Feed mockup ---------- */}
      <div className="mx-auto max-w-sm overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        {/* header */}
        <div className="flex items-center gap-2 px-3 py-2.5">
          <Avatar name={displayPage} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-slate-900">{displayPage}</p>
            <p className="flex items-center gap-1 text-xs text-slate-500">
              Sponsored · <Globe className="h-3 w-3" />
            </p>
          </div>
          <MoreHorizontal className="h-5 w-5 text-slate-400" />
        </div>

        {/* primary text */}
        <div className="px-3 pb-2.5">
          {primary ? (
            <p className="whitespace-pre-wrap text-sm text-slate-800">
              {primary}
              {primaryRaw.length > PRIMARY_TEXT_FOLD && (
                <span className="text-slate-400"> See more</span>
              )}
            </p>
          ) : (
            <p className="text-sm italic text-slate-400">Primary text…</p>
          )}
          {rotationLabel(primaryN, 'primary text') && (
            <p className="mt-1 text-[11px] text-slate-400">{rotationLabel(primaryN, 'primary text')}</p>
          )}
        </div>

        {/* feed asset (1:1) */}
        <div className="aspect-square w-full bg-slate-100">
          <AssetPreview asset={feedAsset} placeholder="Asset preview" />
        </div>

        {/* footer: link / headline / description + CTA */}
        <div className="flex items-center gap-3 bg-slate-50 px-3 py-2.5">
          <div className="min-w-0 flex-1">
            {displayLink && (
              <p className="truncate text-[11px] uppercase tracking-wide text-slate-400">{displayLink}</p>
            )}
            <p className="truncate text-sm font-semibold text-slate-900">
              {headline || <span className="italic text-slate-400">Headline…</span>}
            </p>
            {description && <p className="truncate text-xs text-slate-500">{description}</p>}
            {rotationLabel(headlineN, 'headline') && (
              <p className="text-[11px] text-slate-400">{rotationLabel(headlineN, 'headline')}</p>
            )}
          </div>
          <button
            type="button"
            tabIndex={-1}
            className="shrink-0 cursor-default rounded-md bg-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700"
          >
            {cta}
          </button>
        </div>

        {/* social bar */}
        <div className="flex items-center justify-around border-t border-slate-100 px-3 py-1.5 text-xs text-slate-400">
          <span className="flex items-center gap-1"><ThumbsUp className="h-3.5 w-3.5" /> Like</span>
          <span className="flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" /> Comment</span>
          <span className="flex items-center gap-1"><Share className="h-3.5 w-3.5" /> Share</span>
        </div>
      </div>

      {/* ---------- Story mockup (when a Story asset is attached) ---------- */}
      {hasStory && (
        <div className="mx-auto w-44">
          <p className="mb-1.5 text-center text-xs font-medium text-slate-500">Story / Reels</p>
          <div className="relative aspect-[9/16] w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-900">
            <AssetPreview asset={storyAsset} placeholder="" />
            {/* top: page identity */}
            <div className="absolute left-0 right-0 top-0 flex items-center gap-1.5 bg-gradient-to-b from-black/50 to-transparent px-2 py-2">
              <div className="flex h-5 w-5 items-center justify-center rounded-full bg-white/80 text-[10px] font-semibold text-slate-700">
                {displayPage.charAt(0).toUpperCase()}
              </div>
              <span className="truncate text-[11px] font-medium text-white">{displayPage}</span>
            </div>
            {/* bottom: CTA pill */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 pb-3 pt-6">
              <div className="rounded-full bg-white/90 py-1 text-center text-[11px] font-semibold text-slate-800">
                {cta}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
